import { useState, useCallback, useRef } from "react";
import { Upload, X, FileText, Image, File, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const FILE_ICONS = {
  "application/pdf": <FileText className="w-5 h-5 text-red-500" />,
  "image/jpeg": <Image className="w-5 h-5 text-blue-500" />,
  "image/jpg": <Image className="w-5 h-5 text-blue-500" />,
  "image/png": <Image className="w-5 h-5 text-green-500" />,
  "image/heic": <Image className="w-5 h-5 text-purple-500" />,
  default: <File className="w-5 h-5 text-gray-500" />
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * FileUploadZone - Drag-and-drop multi-file upload component
 * @param {string} entityType - "complaint" or "drc"
 * @param {number} maxFiles - Max number of files (10 for complaints, 20 for DRC)
 * @param {number} maxFileSize - Max individual file size in bytes
 * @param {number} maxTotalSize - Max total size in bytes
 * @param {string[]} allowedExtensions - Array of allowed extensions e.g. [".pdf", ".jpg"]
 * @param {string[]} categories - Category labels for DRC files
 * @param {function} onFilesChange - Callback with array of files
 */
const FileUploadZone = ({
  entityType = "complaint",
  maxFiles = 10,
  maxFileSize = 25 * 1024 * 1024,
  maxTotalSize = 100 * 1024 * 1024,
  allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".docx", ".doc", ".xlsx"],
  categories = [],
  onFilesChange
}) => {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);

  const validateFile = (file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      return `${file.name}: File type ${ext} not allowed`;
    }
    if (file.size > maxFileSize) {
      return `${file.name}: Exceeds ${formatSize(maxFileSize)} limit`;
    }
    return null;
  };

  const addFiles = useCallback((newFiles) => {
    setError("");
    const fileArray = Array.from(newFiles);

    if (files.length + fileArray.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const newTotal = fileArray.reduce((sum, f) => sum + f.size, 0) + totalSize;
    if (newTotal > maxTotalSize) {
      setError(`Total size exceeds ${formatSize(maxTotalSize)}`);
      return;
    }

    const validFiles = [];
    for (const file of fileArray) {
      const err = validateFile(file);
      if (err) {
        setError(err);
        return;
      }
      validFiles.push({ file, category: "", preview: null, id: crypto.randomUUID() });
    }

    // Generate previews for images
    validFiles.forEach((vf) => {
      if (vf.file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles((prev) =>
            prev.map((f) => (f.id === vf.id ? { ...f, preview: e.target.result } : f))
          );
        };
        reader.readAsDataURL(vf.file);
      }
    });

    const updated = [...files, ...validFiles];
    setFiles(updated);
    onFilesChange?.(updated);
  }, [files, maxFiles, maxTotalSize, totalSize, allowedExtensions, maxFileSize, onFilesChange]);

  const removeFile = (id) => {
    const updated = files.filter((f) => f.id !== id);
    setFiles(updated);
    onFilesChange?.(updated);
    setError("");
  };

  const updateCategory = (id, category) => {
    const updated = files.map((f) => (f.id === id ? { ...f, category } : f));
    setFiles(updated);
    onFilesChange?.(updated);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className="space-y-3" data-testid={`file-upload-zone-${entityType}`}>
      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
        }`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="file-drop-zone"
      >
        <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm font-medium">Drag & drop files here, or click to browse</p>
        <p className="text-xs text-muted-foreground mt-1">
          {allowedExtensions.join(", ")} — Max {maxFiles} files, {formatSize(maxFileSize)} each
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={allowedExtensions.join(",")}
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="file-input"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2" data-testid="file-upload-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{files.length} file(s) selected</span>
            <span className="text-xs text-muted-foreground">{formatSize(totalSize)} total</span>
          </div>
          {files.map((f) => (
            <div
              key={f.id}
              className="flex items-center gap-3 p-2 border rounded-md bg-card"
              data-testid={`uploaded-file-${f.id}`}
            >
              {f.preview ? (
                <img src={f.preview} alt="" className="w-10 h-10 object-cover rounded" />
              ) : (
                FILE_ICONS[f.file.type] || FILE_ICONS.default
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.file.name}</p>
                <p className="text-xs text-muted-foreground">{formatSize(f.file.size)}</p>
              </div>
              {categories.length > 0 && (
                <Select value={f.category} onValueChange={(v) => updateCategory(f.id, v)}>
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-red-600"
                onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                data-testid={`remove-file-${f.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
